import { useState, FormEvent } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'

interface ChildInfo {
  id: number
  displayName: string
  username: string
}

interface Props {
  onComplete: (children: ChildInfo[]) => void
  existingChildren?: ChildInfo[]
}

export default function AddChildStep({ onComplete, existingChildren = [] }: Props) {
  const [children, setChildren] = useState<ChildInfo[]>(existingChildren)
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showForm, setShowForm] = useState(existingChildren.length === 0)

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const child = await apiFetch('/onboarding/child', {
        method: 'POST',
        body: JSON.stringify({
          username,
          displayName,
          password,
          birthDate: birthDate ? new Date(birthDate).getTime() : null,
          emailAddress: emailAddress || null,
        }),
      })
      setChildren(prev => [...prev, { id: child.id, displayName: child.displayName, username: child.username }])
      setUsername('')
      setDisplayName('')
      setPassword('')
      setBirthDate('')
      setEmailAddress('')
      setShowForm(false)
    } catch (err: any) {
      setError(err.message || 'Failed to add child.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <MagnumSays>
        {children.length === 0
          ? "Who's going to be reading with me? Add your first reader!"
          : "Nice! Want to add another reader, or are we good to go?"}
      </MagnumSays>

      {children.length > 0 && (
        <div className="space-y-2">
          {children.map(c => (
            <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                {c.displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-sm">{c.displayName}</p>
                <p className="text-xs text-muted-foreground">@{c.username}</p>
              </div>
              <div className="ml-auto text-green-600 text-sm font-medium">Added</div>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <form onSubmit={handleAdd} className="space-y-3">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}

          <div>
            <label htmlFor="ob-childName" className="block text-sm font-medium mb-1">Display Name</label>
            <input
              id="ob-childName"
              type="text"
              required
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="How they'll appear in the app"
            />
          </div>

          <div>
            <label htmlFor="ob-childUser" className="block text-sm font-medium mb-1">Username</label>
            <input
              id="ob-childUser"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="For logging in"
            />
          </div>

          <div>
            <label htmlFor="ob-childPass" className="block text-sm font-medium mb-1">Password</label>
            <input
              id="ob-childPass"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="At least 4 characters"
            />
          </div>

          <div>
            <label htmlFor="ob-childBirth" className="block text-sm font-medium mb-1">
              Birth Date
            </label>
            <input
              id="ob-childBirth"
              type="date"
              required
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label htmlFor="ob-childEmail" className="block text-sm font-medium mb-1">
              Email <span className="text-muted-foreground font-normal">(optional, for goal notifications)</span>
            </label>
            <input
              id="ob-childEmail"
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex gap-2">
            {children.length > 0 && (
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 py-2.5 border border-border rounded-lg font-medium hover:bg-muted transition"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Child'}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <button
            onClick={() => setShowForm(true)}
            className="w-full py-2.5 border border-dashed border-border rounded-lg text-muted-foreground font-medium hover:border-primary hover:text-primary transition"
          >
            + Add another child
          </button>
          <button
            onClick={() => onComplete(children)}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  )
}
